<template>
  <div class="chat-ai-container p-2">
    <!-- Type Selection Screen -->
    <transition mode="out-in" name="fade">
      <chat-type-selector
          v-if="!chatType"
          v-model="selectedChatType"
          @confirm="handleTypeConfirm"
      />

      <!-- Chat Interface -->
      <div v-else class="chat-interface">
        <b-row class="match-height">
          <!-- Left Sidebar - Conversations History -->
          <b-col class="chat-sidebar border-right border-right-secondary" cols="12" lg="3">
            <b-card no-body>
              <b-card-header class="d-flex align-items-center justify-content-between">
                <h5 class="mb-0">
                  <feather-icon class="mr-50" icon="MessageSquareIcon" size="20"/>
                  Conversations
                </h5>
                <b-button
                    v-b-tooltip.hover
                    size="sm"
                    title="Nouvelle conversation"
                    variant="primary"
                    @click="startNewConversation"
                >
                  <feather-icon icon="PlusIcon" size="18"/>
                </b-button>
              </b-card-header>

              <b-card-body class="conversations-list p-0 overflow-y-scroll">
                <div v-if="isLoadingConversations" class="text-center p-2">
                  <b-spinner small variant="primary"/>
                  <p class="text-muted mb-0 mt-1">Chargement...</p>
                </div>

                <b-list-group v-else flush>
                  <b-list-group-item
                      v-for="conversation in conversations"
                      :key="conversation.uid"
                      :active="currentConversation && currentConversation.uid === conversation.uid"
                      button
                      class="conversation-item"
                      @click="selectConversation(conversation.uid)"
                  >
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="conversation-info">
                        <h6 class="mb-25 text-nowrap overflow-hidden text-truncate">
                          {{ conversation.title || getConversationTitle(conversation.type) }}
                        </h6>
                        <small class="text-muted">
                          {{ formatDate(conversation.updated_at) }}
                        </small>
                      </div>
                      <b-dropdown
                          no-caret
                          right
                          size="sm"
                          variant="link"
                          @click.stop
                      >
                        <template #button-content>
                          <feather-icon icon="MoreVerticalIcon" size="16"/>
                        </template>
                        <b-dropdown-item @click="showEditConversationModal = true">
                          <feather-icon class="mr-50" icon="EditIcon" size="14"/>
                          Modifier
                        </b-dropdown-item>
                        <b-dropdown-item @click="deleteConversationConfirm(conversation.uid)">
                          <feather-icon class="mr-50" icon="Trash2Icon" size="14"/>
                          Supprimer
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                  </b-list-group-item>
                </b-list-group>

                <div v-if="conversations.length === 0" class="text-center p-3">
                  <p class="text-muted mb-0">Aucune conversation</p>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Main Chat Area -->
          <b-col cols="12" lg="9">
            <b-card class="chat-card d-flex flex-column" no-body>
              <!-- Chat Header -->
              <b-card-header class="d-flex align-items-center justify-content-between">
                <div class="chat-header-info d-flex flex-column">
                  <h5 class="mb-0">
                    <b-badge :variant="chatType === 'rapport' ? 'light-primary' : 'light-success'">
                      <feather-icon
                          :icon="chatType === 'rapport' ? 'FileTextIcon' : 'UserIcon'"
                          class="mr-25"
                          size="14"
                      />
                      {{ chatType === 'rapport' ? 'Génération de Rapport' : 'Génération de CV' }}
                    </b-badge>
                  </h5>
                  <small class="text-muted">
                    {{ (currentConversation && currentConversation.title) || 'Nouvelle conversation' }}
                  </small>
                </div>

                <div class="chat-header-actions d-flex">
                  <b-button
                      v-b-tooltip.hover
                      :title="showFileUploader ? 'Masquer l\'upload' : 'Afficher l\'upload'"
                      size="sm"
                      variant="flat-secondary"
                      @click="showFileUploader = !showFileUploader"
                  >
                    <feather-icon icon="UploadIcon" size="18"/>
                  </b-button>
                  <b-button
                      v-b-tooltip.hover
                      size="sm"
                      title="Réinitialiser le chat"
                      variant="flat-danger"
                      @click="resetChat"
                  >
                    <feather-icon icon="RefreshCwIcon" size="18"/>
                  </b-button>
                </div>
              </b-card-header>

              <!-- File Uploader (Collapsible) -->
              <b-collapse v-model="showFileUploader">
                <b-card-body class="border-bottom">
                  <chat-file-uploader
                      ref="fileUploader"
                      v-model="uploadedFiles"
                      :chat-type="chatType"
                      :multiple="chatType === 'rapport'"
                      @remove="handleFileRemove"
                      @upload="handleFileUpload"
                  />
                </b-card-body>
              </b-collapse>

              <!-- Messages List -->
              <div class="chat-messages-container overflow-hidden">
                <chat-messages-list
                    :chat-type="chatType"
                    :is-streaming="isStreaming"
                    :is-typing="false"
                    :messages="allMessages"
                    @regenerate="handleRegenerate"
                />
              </div>

              <!-- Chat Input -->
              <div class="chat-input-container bg-white">
                <chat-input
                    ref="chatInput"
                    v-model="messageInput"
                    :disabled="isLoading || !currentConversation"
                    :is-streaming="isStreaming"
                    :placeholder="inputPlaceholder"
                    :show-hints="false"
                    @attach="showFileUploader = true"
                    @send="handleSendMessage"
                    @stop="handleStopStreaming"
                />
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </transition>
    <chat-edit-modal
        v-if="currentConversation"
        :visible="showEditConversationModal"
        :current-title="currentConversation.title"
        @save="editConversation"
        @cancel="showEditConversationModal = false"
    />
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BCollapse,
  BDropdown,
  BDropdownItem,
  BListGroup,
  BListGroupItem,
  BRow,
  BSpinner,
  VBTooltip
} from 'bootstrap-vue'
import ChatTypeSelector from '@/views/apps/chat/components/ChatTypeSelector.vue'
import ChatFileUploader from '@/views/apps/chat/components/ChatFileUploader.vue'
import ChatMessagesList from '@/views/apps/chat/components/ChatMessagesList.vue'
import ChatInput from '@/views/apps/chat/components/ChatInput.vue'
import ChatEditModal from "@/views/apps/chat/components/ChatEditModal.vue";

import moment from "moment";
moment.locale('fr-FR')

export default {
  name: 'ChatAI',
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BSpinner,
    BListGroup,
    BListGroupItem,
    BDropdown,
    BDropdownItem,
    BBadge,
    BCollapse,
    ChatEditModal,
    ChatTypeSelector,
    ChatFileUploader,
    ChatMessagesList,
    ChatInput
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  data() {
    return {
      selectedChatType: null,
      chatType: null,
      messageInput: '',
      showFileUploader: true,
      uploadedFiles: [],
      isLoadingConversations: false,
      showEditConversationModal: false,
    }
  },
  computed: {
    ...mapState('chat', [
      'conversations',
      'currentConversation',
      'messages',
      'isLoading',
      'isStreaming',
      'files',
      'error'
    ]),
    ...mapGetters('chat', [
      'allMessages'
    ]),
    inputPlaceholder() {
      if (!this.currentConversation) {
        return 'Créez une conversation pour commencer...'
      }
      return this.chatType === 'rapport'
          ? 'Posez vos questions sur le rapport...'
          : 'Répondez aux questions pour créer votre CV...'
    }
  },
  watch: {
    error(newError) {
      if (newError) {
        this.showErrorToast(newError)
      }
    }
  },
  mounted() {
    this.loadConversations()
  },
  beforeDestroy() {
    this.resetChat()
  },
  methods: {
    ...mapActions('chat', [
      'fetchConversations',
      'fetchConversation',
      'createConversation',
      'uploadFiles',
      'sendMessage',
      'stopStreaming',
      'updateConversation',
      'deleteConversation',
      'resetChat'
    ]),

    async loadConversations() {
      this.isLoadingConversations = true
      try {
        await this.fetchConversations()
      } catch (error) {
        console.error('Error loading conversations:', error)
      } finally {
        this.isLoadingConversations = false
      }
    },

    async handleTypeConfirm(type) {
      this.chatType = type
      this.showFileUploader = true

      // Create initial conversation
      try {
        await this.createConversation({
          type: type,
          title: this.getConversationTitle(type)
        })

        this.$nextTick(() => {
          if (this.$refs.chatInput) {
            this.$refs.chatInput.focus()
          }
        })
      } catch (error) {
        console.error('Error creating conversation:', error)
        this.showErrorToast('Erreur lors de la création de la conversation')
      }
    },

    async selectConversation(conversationId) {
      try {
        await this.fetchConversation(conversationId)
        this.chatType = this.currentConversation.type
        this.uploadedFiles = this.files || []
      } catch (error) {
        console.error('Error selecting conversation:', error)
        this.showErrorToast('Erreur lors du chargement de la conversation')
      }
    },

    async handleFileUpload(files) {
      if (!this.currentConversation) {
        this.showErrorToast('Veuillez créer une conversation d\'abord')
        return
      }

      try {
        const fileObjects = files.map(f => f.file)
        const uploadedFiles = await this.uploadFiles(fileObjects)

        // Update file status in uploader
        files.forEach((file) => {
          if (this.$refs.fileUploader) {
            this.$refs.fileUploader.updateFileStatus(file.id, 'success')
          }
        })

        this.showSuccessToast(`${uploaded.length} fichier(s) chargé(s) avec succès`)
      } catch (error) {
        console.error('Error uploading files:', error)
        files.forEach(file => {
          if (this.$refs.fileUploader) {
            this.$refs.fileUploader.updateFileStatus(file.id, 'error')
          }
        })
        this.showErrorToast('Erreur lors du chargement des fichiers')
      }
    },

    handleFileRemove(file) {
      // Handle file removal if needed
      console.log('File removed:', file)
    },

    async handleSendMessage(message) {
      if (!this.currentConversation) {
        this.showErrorToast('Veuillez créer une conversation d\'abord')
        return
      }

      try {
        await this.sendMessage(message)
        this.messageInput = ''
      } catch (error) {
        console.error('Error sending message:', error)
        this.showErrorToast('Erreur lors de l\'envoi du message')
      }
    },

    handleStopStreaming() {
      this.stopStreaming()
      this.showInfoToast('Génération arrêtée')
    },

    handleRegenerate(messageId) {
      // TODO: Implement regenerate functionality
      console.log('Regenerate message:', messageId)
      this.showInfoToast('Fonctionnalité de régénération à venir')
    },

    async startNewConversation() {
      this.chatType = null
      this.selectedChatType = null
      this.messageInput = ''
      this.uploadedFiles = []
      this.showFileUploader = true
      await this.resetChat()
    },

    async editConversation(newTitle) {
      if (!this.currentConversation) {
        this.showErrorToast('Aucune conversation disponible pour modification.')
        return
      }

      try {
        await this.updateConversation({title: newTitle})
        await this.loadConversations()
      } catch (error) {
        console.error('Error sending message:', error)
        this.showErrorToast('Erreur lors de la modification de la conversation.')
      }
    },

    async deleteConversationConfirm(conversationId) {
      const swal = this.$swal;

      swal.fire({
        title: 'Supprimer la conversation ?',
        text: 'Cette action est irréversible',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1'
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.deleteConversation(conversationId)
            await this.loadConversations()
            this.showSuccessToast('Conversation supprimée')

            // If deleted current conversation, reset
            if (this.currentConversation && this.currentConversation.uid === conversationId) {
              this.startNewConversation()
            }
          } catch (error) {
            console.error('Error deleting conversation:', error)
            this.showErrorToast('Erreur lors de la suppression')
          }
        }
      })
    },

    getConversationTitle(type) {
      return type === 'rapport' ? 'Nouveau Rapport' : 'Nouveau CV'
    },

    formatDate(date) {
      if(date === '' || date === null) return moment().fromNow();

      return moment(date, 'YYYY-MM-DD H:m:s').fromNow();
    },

    showSuccessToast(message) {
      this.$toast({
        component: this.$createElement('div', {}, [
          this.$createElement('feather-icon', {
            props: {icon: 'CheckIcon'},
            class: 'mr-50'
          }),
          message
        ]),
        props: {
          variant: 'success'
        }
      })
    },

    showErrorToast(error) {
      const message = (error && error.message) || error || 'Une erreur est survenue'
      this.$toast({
        component: this.$createElement('div', {}, [
          this.$createElement('feather-icon', {
            props: {icon: 'XIcon'},
            class: 'mr-50'
          }),
          message
        ]),
        props: {
          variant: 'danger'
        }
      })
    },

    showInfoToast(message) {
      this.$toast({
        component: this.$createElement('div', {}, [
          this.$createElement('feather-icon', {
            props: {icon: 'InfoIcon'},
            class: 'mr-50'
          }),
          message
        ]),
        props: {
          variant: 'info'
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.conversations-list {
  max-height: calc(100vh - 10rem);
}

.conversation-item {
  transition: all 0.3s ease;

  &:hover {
    border-left: 0.2rem solid var(--primary);
    background-color: color-mix(in srgb, var(--primary) 10%, transparent);
  }

  &.active {
    background-color: color-mix(in srgb, var(--primary) 10%, transparent) !important;

    small {
      color: var(--secondary) !important;
    }
  }
}

.chat-card {
  height: calc(100vh - 10rem);
}

.chat-header-info {
  gap: 0.25rem;
}

.chat-header-actions {
  gap: 0.5rem;
}

.chat-messages-container {
  flex: 1;
  background: #fafafa;
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Responsive
@media (max-width: 992px) {
  .chat-sidebar {
    margin-bottom: 1rem;

    .card {
      border-right: none;
      border-bottom: 1px solid #ebe9f1;
    }
  }

  .conversations-list {
    max-height: 300px;
  }

  .chat-card {
    height: calc(100vh - 550px);
  }
}

@media (max-width: 576px) {
  .chat-ai-container {
    padding: 0.75rem;
  }

  .chat-card {
    height: calc(100vh - 400px);
  }
}
</style>
